import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * Gates protected routes behind auth. While AuthContext is still bootstrapping
 * (i.e. `GET /auth/me` is in-flight) we show a dark "Loading…" splash. If the
 * bootstrap hangs for >10s we surface a "Still trying…" message plus a Retry
 * button so the user can never be permanently stuck on this screen.
 */
export default function ProtectedRoute({ children }) {
  const { user, loading, checkAuth } = useAuth();
  const [stuck, setStuck] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!loading) { setStuck(false); return; }
    setStuck(false);
    const t = setTimeout(() => setStuck(true), 10000);
    return () => clearTimeout(t);
  }, [loading, retryCount]);

  const handleRetry = () => {
    setRetryCount((n) => n + 1);
    checkAuth?.();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0d1117] text-zinc-400 p-6">
        <div className="text-center space-y-3 max-w-sm">
          <div className="font-mono text-xs" data-testid="auth-loading">Loading…</div>
          {stuck && (
            <div data-testid="auth-loading-stuck" className="space-y-3 pt-2 border-t border-zinc-800">
              <div className="text-[11px] font-mono text-amber-400">
                Still trying to reach the server…
              </div>
              <div className="text-[11px] text-zinc-500 leading-relaxed">
                This usually means the backend is slow or your session expired.
                You can retry, or head to the login page to start fresh.
              </div>
              <div className="flex items-center gap-2 justify-center">
                <button
                  type="button"
                  onClick={handleRetry}
                  data-testid="auth-loading-retry"
                  className="text-[11px] font-mono bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/40 text-amber-300 rounded px-3 py-1.5 transition-colors"
                >
                  Retry
                </button>
                <a
                  href="/login"
                  data-testid="auth-loading-login"
                  className="text-[11px] font-mono bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 rounded px-3 py-1.5 transition-colors"
                >
                  Go to Login
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}
